import "./ThinkingImageTicker.css";
import { For, createEffect, onCleanup, Show } from "solid-js";
import { createStore } from "solid-js/store";
import type { ThinkingImageTickerItem } from "../chatPanelState.ts";

const IMAGE_ENTER_ANIMATION_MS = 150;
const IMAGE_PRE_SCROLL_MS = 100;
const IMAGE_ENTER_GAP_START_REM = 2;
const IMAGE_GAP_REM = 0.25;
const IMAGE_SIZE_REM = 2;
const IMAGE_ENTER_SIZE_START_REM = 0.75;
const IMAGE_ENTER_SCROLL_RESERVE_REM =
	IMAGE_ENTER_GAP_START_REM - IMAGE_GAP_REM;

type DisplayedImage = ThinkingImageTickerItem & {
	phase: "pre-enter" | "entering" | "settled";
};

const remToPx = (rem: number, element: HTMLElement) =>
	rem * Number.parseFloat(getComputedStyle(element).fontSize);

export const ThinkingImageTicker = (props: {
	images: readonly ThinkingImageTickerItem[];
}) => {
	const [displayedImages, setDisplayedImages] = createStore<DisplayedImage[]>(
		[],
	);
	let scrollEl: HTMLDivElement | undefined;

	let consumedCount = 0;
	let pendingImages: ThinkingImageTickerItem[] = [];
	let processing = false;
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	let scrollAnimationFrameId: number | undefined;

	const clearTimer = () => {
		if (timeoutId !== undefined) {
			clearTimeout(timeoutId);
			timeoutId = undefined;
		}
	};

	const cancelScrollAnimation = () => {
		if (scrollAnimationFrameId !== undefined) {
			cancelAnimationFrame(scrollAnimationFrameId);
			scrollAnimationFrameId = undefined;
		}
	};

	const delay = (ms: number) =>
		new Promise<void>((resolve) => {
			clearTimer();
			timeoutId = setTimeout(() => {
				timeoutId = undefined;
				resolve();
			}, ms);
		});

	const animateScrollLeft = (
		element: HTMLDivElement,
		to: number,
		durationMs: number,
	) =>
		new Promise<void>((resolve) => {
			cancelScrollAnimation();
			const from = element.scrollLeft;
			const start = performance.now();

			const step = (now: number) => {
				const progress = Math.min(1, (now - start) / durationMs);
				const eased = 1 - (1 - progress) ** 2;
				element.scrollLeft = from + (to - from) * eased;

				if (progress < 1) {
					scrollAnimationFrameId = requestAnimationFrame(step);
				} else {
					scrollAnimationFrameId = undefined;
					resolve();
				}
			};

			scrollAnimationFrameId = requestAnimationFrame(step);
		});

	const runPrePhase = async (targetScrollLeft: number) => {
		if (!scrollEl) {
			await delay(IMAGE_PRE_SCROLL_MS);
			return;
		}

		if (Math.abs(scrollEl.scrollLeft - targetScrollLeft) > 1) {
			await animateScrollLeft(
				scrollEl,
				targetScrollLeft,
				IMAGE_PRE_SCROLL_MS,
			);
			return;
		}

		scrollEl.scrollLeft = targetScrollLeft;
		await delay(IMAGE_PRE_SCROLL_MS);
	};

	const setImagePhase = (
		imageId: string,
		phase: DisplayedImage["phase"],
	) => {
		const index = displayedImages.findIndex((item) => item.id === imageId);
		if (index >= 0) {
			setDisplayedImages(index, "phase", phase);
		}
	};

	const runEnterAnimation = (imageId: string) => {
		requestAnimationFrame(() => {
			requestAnimationFrame(() => {
				setImagePhase(imageId, "entering");

				timeoutId = setTimeout(() => {
					setImagePhase(imageId, "settled");
					showNextImage();
				}, IMAGE_ENTER_ANIMATION_MS);
			});
		});
	};

	const showNextImage = () => {
		const image = pendingImages.shift();
		if (!image) {
			processing = false;
			return;
		}

		setDisplayedImages((items) => [
			...items,
			{ ...image, phase: "pre-enter" },
		]);

		requestAnimationFrame(() => {
			const targetScrollLeft = scrollEl
				? Math.max(
						0,
						scrollEl.scrollWidth -
							scrollEl.clientWidth -
							remToPx(IMAGE_ENTER_SCROLL_RESERVE_REM, scrollEl),
					)
				: 0;

			void runPrePhase(targetScrollLeft).then(() =>
				runEnterAnimation(image.id),
			);
		});
	};

	const enqueueImages = () => {
		const images = props.images;
		if (images.length > consumedCount) {
			pendingImages.push(...images.slice(consumedCount));
			consumedCount = images.length;
		}

		if (!processing && pendingImages.length > 0) {
			processing = true;
			showNextImage();
		}
	};

	createEffect(() => {
		enqueueImages();
	});

	onCleanup(() => {
		clearTimer();
		cancelScrollAnimation();
		processing = false;
		pendingImages = [];
	});

	return (
		<Show when={displayedImages.length > 0}>
			<div
				class="thinking-image-ticker"
				ref={(el) => {
					scrollEl = el;
				}}
				style={{
					"--thinking-image-enter-duration": `${IMAGE_ENTER_ANIMATION_MS}ms`,
					"--thinking-image-enter-gap-start": `${IMAGE_ENTER_GAP_START_REM}rem`,
					"--thinking-image-enter-size-start": `${IMAGE_ENTER_SIZE_START_REM}rem`,
					"--thinking-image-gap": `${IMAGE_GAP_REM}rem`,
					"--thinking-image-size": `${IMAGE_SIZE_REM}rem`,
				}}
			>
				<div class="thinking-image-ticker__track">
					<For each={displayedImages}>
						{(image) => (
							<div
								class="thinking-image-ticker__item"
								classList={{
									"is-pre-enter": image.phase === "pre-enter",
									"is-entering": image.phase === "entering",
								}}
							>
								<div class="thinking-image-ticker__image-slot">
									<img
										classList={{
											"thinking-image-ticker__image--circle":
												image.shape === "circle",
											"thinking-image-ticker__image--rounded":
												image.shape === "rounded",
										}}
										src={image.url}
										alt=""
										decoding="async"
									/>
								</div>
							</div>
						)}
					</For>
				</div>
			</div>
		</Show>
	);
};
